import React, { ComponentType, useMemo, useRef } from 'react'
import { Col, Container, OverlayTrigger, Row } from 'react-bootstrap'
import BootstrapTooltip from 'react-bootstrap/Tooltip'

import { getCitationNodesFromEditorState } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Nodes/CitationNode/CitationNode'
import { CitationNodeContext } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Nodes/CitationNode/CitationNodeContext'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { MeetingPrepInteractionResult, MeetingPrepResult } from '../../models/MeetingPrep'
import { AIAgentType } from '../../models/AIAgent'
import { Tooltip } from '../../components/Tooltip/Tooltip'
import { Button } from '../../components/Button/Button'

import { EditableAIAgentResult, EditButtonComponentProps } from './EditableAIAgentResult/EditableAIAgentResult'
import { MeetingPrepInteractionPayload } from './hooks/useMeetingPrep'
import { useAIAgentResultCopy } from './hooks/useAIAgentResultCopy'
import { useAIAgentResultState } from './hooks/useAIAgentResultState'
import { AIAgentLoader } from './AIAgentLoader/AIAgentLoader'
import { AIAgentResultCitations, Citation } from './AIAgentResultCitations/AIAgentResultCitations'

import './MeetingPrep.scss'
import { StreamingAIAgentChat } from './Chat/StreamingAIAgentChat'

export const MEETING_PREP_PDF_STYLES = `
      .meeting-prep-container {
        height: auto !important;
      }
    `

export interface FooterComponentProps {
  meetingPrepBody: React.RefObject<HTMLElement> | null
}

interface MeetingPrepBodyProps {
  isLoading: boolean
  interactionIsLoading: boolean
  editIsLoading: boolean
  meetingPrep: MeetingPrepResult | null
  onMeetingPrepChatInteraction: (chatMessage: string) => void
  handleResubmitInteraction: () => Promise<void>
  onMeetingPrepEditInteraction: (payload: MeetingPrepInteractionPayload) => Promise<MeetingPrepInteractionResult | undefined>
  trackingEventDetails: any
  HeaderComponent?: ComponentType | null
  FooterComponent?: ComponentType<FooterComponentProps> | null
  citations: Citation[] | null
  citationIsLoading: boolean
}

export const MeetingPrepBody: React.FC<MeetingPrepBodyProps> = ({
  isLoading,
  interactionIsLoading,
  editIsLoading,
  meetingPrep,
  onMeetingPrepChatInteraction,
  handleResubmitInteraction,
  onMeetingPrepEditInteraction,
  trackingEventDetails,
  HeaderComponent,
  FooterComponent,
  citations = [],
  citationIsLoading,
}) => {
  const { tracked } = useTracking()
  const exportPDFRef = useRef(null)

  const meetingPrepDocumentState = useAIAgentResultState(meetingPrep?.document)
  const meetingPrepEmailState = useAIAgentResultState(meetingPrep?.email)

  const filteredCitations = useMemo(() => {
    if (!citations?.length) {
      return []
    }
    const citationIds = new Set(
      [meetingPrepDocumentState, meetingPrepEmailState].flatMap((state) =>
        getCitationNodesFromEditorState(state.editorState).map((node) => node.getCitationId()),
      ),
    )
    return citations.filter((citation) => citationIds.has(citation.id))
  }, [citations, meetingPrepDocumentState, meetingPrepEmailState])

  const { handleCopy: _handleDocumentCopy } = useAIAgentResultCopy({ aiAgentResultState: meetingPrepDocumentState })
  const { handleCopy: _handleEmailCopy, handleMailToClicked: _handleMailToClicked } = useAIAgentResultCopy({ aiAgentResultState: meetingPrepEmailState })

  const handleChatInteraction = tracked(
    onMeetingPrepChatInteraction,
    {
      eventName: TrackEvent.MEETING_PREP_INTERACTED_WITH,
      eventDetails: { ...trackingEventDetails, type: 'chat' },
    },
    {
      eventDetailsExtractor: (chatMessage: string) => ({
        chatMessage,
      }),
    },
  )

  const _handleDocumentEditInteraction = async (editedMeetingPrepDocument: string) => {
    return await onMeetingPrepEditInteraction({ type: 'documentEdit', document: editedMeetingPrepDocument })
  }

  const handleDocumentEditInteraction = tracked(_handleDocumentEditInteraction, {
    eventName: TrackEvent.MEETING_PREP_INTERACTED_WITH,
    eventDetails: { ...trackingEventDetails, type: 'documentEdit' },
  })

  const _handleEmailEditInteraction = async (editedMeetingPrepEmail: string) => {
    return await onMeetingPrepEditInteraction({ type: 'emailEdit', email: editedMeetingPrepEmail })
  }

  const handleEmailEditInteraction = tracked(_handleEmailEditInteraction, {
    eventName: TrackEvent.MEETING_PREP_INTERACTED_WITH,
    eventDetails: { ...trackingEventDetails, type: 'emailEdit' },
  })

  const handleDocumentCopy = tracked(_handleDocumentCopy, {
    eventName: TrackEvent.MEETING_PREP_COPIED,
    eventDetails: trackingEventDetails,
  })

  const handleEmailCopy = tracked(_handleEmailCopy, {
    eventName: TrackEvent.MEETING_PREP_EMAIL_COPIED,
    eventDetails: trackingEventDetails,
  })

  const handleMailToClicked = tracked(_handleMailToClicked, {
    eventName: TrackEvent.MEETING_PREP_MAILTO_CLICKED,
    eventDetails: trackingEventDetails,
  })

  const EmailResultActionButtonsComponent = () => {
    return (
      <>
        <Button className='button-large p-2' variant='primary' onClick={handleMailToClicked}>
          <i className='bi bi-send' />
          <span className='button-small'>Send</span>
        </Button>
        <Button className='button-large p-2' variant='primary' onClick={handleEmailCopy}>
          <i className='bi bi-copy' />
          <span className='button-small'>Copy</span>
        </Button>
      </>
    )
  }

  const DocumentResultActionButtonsComponent = () => {
    return (
      <Button className='button-large p-2' variant='primary' onClick={handleDocumentCopy}>
        <i className='bi bi-copy' />
        <span className='button-small'>Copy</span>
      </Button>
    )
  }

  const EditButtonComponent: React.FC<EditButtonComponentProps> = ({ handleClick }) => {
    return (
      <OverlayTrigger
        trigger={citationIsLoading ? ['hover', 'focus'] : []}
        placement='top'
        overlay={<BootstrapTooltip className='zocks-text-gray-500'>Editing is disabled while references are being generated. Please hold on.</BootstrapTooltip>}
      >
        <Button
          className='button-large p-2'
          variant='primary'
          onClick={handleClick}
          disabled={isLoading || interactionIsLoading || citationIsLoading || editIsLoading}
        >
          <i className='bi bi-pencil' />
          <span className='button-small'>Edit</span>
        </Button>
      </OverlayTrigger>
    )
  }

  if (isLoading) {
    return (
      <AIAgentLoader
        className='meeting-prep-loader-inner-container'
        longLoadingMessage='Meeting Preparations with a large context might take up to 45 seconds to generate, please hold on'
      />
    )
  }

  return (
    <Container className='d-flex flex-column'>
      {HeaderComponent ? <HeaderComponent /> : null}
      <Row>
        <Col xs={12} lg={8} ref={exportPDFRef}>
          <div className='p-3 zocks-bg-color-gray-300 zocks-border-1.0 meeting-prep-container'>
            <div className='d-flex flex-column gap-3 h-100 meeting-prep-result-container overflow-y-auto overflow-x-hidden'>
              {meetingPrep?.email ? (
                <CitationNodeContext.Provider value={{ citations: isLoading ? [] : filteredCitations }}>
                  <EditableAIAgentResult
                    label='E-mail'
                    aiAgentResultState={meetingPrepEmailState}
                    handleInteraction={handleEmailEditInteraction}
                    isLoading={isLoading}
                    interactionIsLoading={interactionIsLoading || editIsLoading}
                    collapsible={true}
                    collapseByDefault={true}
                    ActionButtonsComponent={EmailResultActionButtonsComponent}
                    EditButtonComponent={EditButtonComponent}
                  />
                </CitationNodeContext.Provider>
              ) : null}
              {meetingPrep?.document ? (
                <CitationNodeContext.Provider value={{ citations: isLoading ? [] : filteredCitations }}>
                  <EditableAIAgentResult
                    label='Summary'
                    aiAgentResultState={meetingPrepDocumentState}
                    handleInteraction={handleDocumentEditInteraction}
                    isLoading={isLoading}
                    interactionIsLoading={interactionIsLoading || editIsLoading}
                    collapsible={true}
                    ActionButtonsComponent={DocumentResultActionButtonsComponent}
                    EditButtonComponent={EditButtonComponent}
                  />
                </CitationNodeContext.Provider>
              ) : null}
              <AIAgentResultCitations aiAgentType={AIAgentType.MEETING_PREPARATION} citationIsLoading={citationIsLoading} citations={filteredCitations} />
            </div>
          </div>
        </Col>
        <Col xs={12} lg={4}>
          <StreamingAIAgentChat
            handleSubmit={handleChatInteraction}
            isLoading={interactionIsLoading}
            disabled={interactionIsLoading || editIsLoading || citationIsLoading}
            handleResubmit={handleResubmitInteraction}
          />
        </Col>
      </Row>
      {FooterComponent ? <FooterComponent meetingPrepBody={exportPDFRef} /> : null}
    </Container>
  )
}

export const MeetingPrepModalHeader = () => {
  return (
    <Col className='d-flex gap-2 align-items-center justify-content-center'>
      <h4>Meeting preparation</h4>
      <Tooltip className='zocks-text-gray-500' placement='right' title='Click here for more information about the meeting preparation'>
        <a
          target='_blank'
          href='https://help.zocks.io/en/articles/9649052-meeting-preparation'
          className='button-link-medium zocks-text-gray-900'
          rel='noreferrer'
        >
          <i className='bi bi-info-circle' />
        </a>
      </Tooltip>
    </Col>
  )
}
